import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useUpdateCarrierService,
  type CarrierServiceListItem,
} from "@/api/carrier-services";
import { toast } from "sonner";

/**
 * Phase C (#265): per-service package fit ceilings (max weight, max longest
 * side, max length+girth). All optional — blank = no limit enforced. When a
 * package violates any set ceiling the quote short-circuits to
 * PACKAGE_EXCEEDS_LIMITS before any rate-band lookup happens.
 */
interface EditCarrierServiceLimitsDialogProps {
  open: boolean;
  onClose: () => void;
  service: CarrierServiceListItem;
}

interface FormState {
  maxWeightKg: string;
  maxLongestSideCm: string;
  maxLengthPlusGirthCm: string;
}

function toFormState(s: CarrierServiceListItem): FormState {
  const fmt = (v: string | number | null | undefined) =>
    v == null ? "" : String(Number(v));
  return {
    maxWeightKg: fmt(s.maxWeightKg),
    maxLongestSideCm: fmt(s.maxLongestSideCm),
    maxLengthPlusGirthCm: fmt(s.maxLengthPlusGirthCm),
  };
}

export function EditCarrierServiceLimitsDialog({
  open,
  onClose,
  service,
}: EditCarrierServiceLimitsDialogProps) {
  const [form, setForm] = useState<FormState>(toFormState(service));

  // Re-sync if a different service row is opened without unmounting.
  useEffect(() => {
    setForm(toFormState(service));
  }, [service]);

  const updateMutation = useUpdateCarrierService({
    onSuccess: () => {
      toast.success("Package limits saved", {
        description: `${service.code} updated`,
      });
      onClose();
    },
    onError: (err) => {
      toast.error("Failed to save", { description: err.message });
    },
  });

  const parse = (s: string): number | null => {
    if (s.trim() === "") return null;
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const submit = () => {
    updateMutation.mutate({
      id: service.id,
      input: {
        maxWeightKg: parse(form.maxWeightKg),
        maxLongestSideCm: parse(form.maxLongestSideCm),
        maxLengthPlusGirthCm: parse(form.maxLengthPlusGirthCm),
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Package limits — {service.name}</DialogTitle>
          <DialogDescription>
            Leave a field blank to skip that check. Packages exceeding any set
            ceiling are rejected at quote time with{" "}
            <code className="font-mono text-xs">PACKAGE_EXCEEDS_LIMITS</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="max-weight">Max weight (kg)</Label>
            <Input
              id="max-weight"
              type="number"
              min={0.1}
              step="0.1"
              value={form.maxWeightKg}
              onChange={(e) =>
                setForm({ ...form, maxWeightKg: e.target.value })
              }
              placeholder="30"
            />
            <p className="text-xs text-muted-foreground mt-1">
              The service&apos;s heaviest accepted package.
            </p>
          </div>
          <div>
            <Label htmlFor="max-longest">Max longest side (cm)</Label>
            <Input
              id="max-longest"
              type="number"
              min={1}
              step="1"
              value={form.maxLongestSideCm}
              onChange={(e) =>
                setForm({ ...form, maxLongestSideCm: e.target.value })
              }
              placeholder="120"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Longest of length, width, or height.
            </p>
          </div>
          <div>
            <Label htmlFor="max-lg">Max length + girth (cm)</Label>
            <Input
              id="max-lg"
              type="number"
              min={1}
              step="1"
              value={form.maxLengthPlusGirthCm}
              onChange={(e) =>
                setForm({ ...form, maxLengthPlusGirthCm: e.target.value })
              }
              placeholder="300"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Longest side + 2 × (sum of the other two). Standard oversize
              metric across carriers (Canada Post, UPS, FedEx).
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
